import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { SliderControlIcon } from "@repo/ui-parkmobile/icons/SliderControl";

export const slider = tv({
  base: cn("uipm:relative uipm:w-full uipm:flex uipm:flex-col uipm:gap-2"),
  variants: {
    state: {
      default: "",
      disabled: "uipm:opacity-50",
    },
  },
  defaultVariants: {
    state: "default",
  },
});

export const sliderTrack = tv({
  base: cn(
    "uipm:relative uipm:w-full uipm:h-8 uipm:bg-[var(--surface-surface-secondary)] uipm:rounded-lg uipm:overflow-hidden"
  ),
});

export const sliderFill = tv({
  base: cn(
    "uipm:absolute uipm:h-full uipm:rounded-lg uipm:transition-all uipm:bg-[var(--surface-surface-accent-tertiary)]"
  ),
});

export const sliderThumb = tv({
  base: cn(
    "uipm:absolute uipm:top-1/2 uipm:-translate-y-1/2",
    "uipm:w-9 uipm:h-10 uipm:cursor-pointer uipm:transition-all uipm:pointer-events-none",
    "uipm:hover:scale-105 uipm:focus-visible:scale-105",
    "uipm:focus-visible:outline-none uipm:focus-visible:ring-2 uipm:focus-visible:ring-[var(--color-color-accent)] uipm:focus-visible:ring-offset-2",
    "uipm:-translate-x-1/2 uipm:z-10"
  ),
  variants: {
    state: {
      default: "",
      disabled:
        "uipm:cursor-not-allowed uipm:hover:scale-100 uipm:focus-visible:scale-100 uipm:opacity-50",
    },
  },
  defaultVariants: {
    state: "default",
  },
});

export type SliderProps = VariantProps<typeof slider> & {
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  label?: React.ReactNode;
  showValue?: boolean;
  disabled?: boolean;
  className?: string;
};

export function Slider({
  min = 0,
  max = 100,
  step = 1,
  value,
  defaultValue = 0,
  onChange,
  label,
  showValue = false,
  disabled,
  state,
  className,
}: SliderProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const currentValue = value !== undefined ? value : internalValue;
  const percentage = ((currentValue - min) / (max - min)) * 100;
  const sliderState = disabled ? "disabled" : state;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  return (
    <div className={cn(slider({ state: sliderState }), className)}>
      {(label || showValue) && (
        <div className="uipm:flex uipm:justify-between uipm:items-center uipm:mb-2">
          {label && (
            <span className="uipm:text-base uipm:text-[var(--typography-colors-text-body)] uipm:font-['Inter']">
              {label}
            </span>
          )}
          {showValue && (
            <span className="uipm:text-base uipm:text-[var(--typography-colors-text-body)] uipm:font-['Inter']">
              {currentValue}
            </span>
          )}
        </div>
      )}
      <div className="uipm:relative">
        <div className={sliderTrack()}>
          <div
            className={sliderFill()}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={currentValue}
          onChange={handleChange}
          disabled={disabled}
          className="uipm:absolute uipm:inset-0 uipm:w-full uipm:h-full uipm:opacity-0 uipm:cursor-pointer uipm:disabled:cursor-not-allowed uipm:z-20"
          style={{
            background: 'transparent',
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
          }}
        />
        <div
          className={sliderThumb({ state: sliderState })}
          style={{ left: `${percentage}%` }}
        >
          <SliderControlIcon
            width={36}
            height={40}
          />
        </div>
      </div>
    </div>
  );
}